/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import {
  KgDirtree,
  KgSummary, GlobalGraphResponse, KgDoc, RepoGraphEntry, RepoHealthStatus, shaMatches } from './types';

export type KgRepoMeta = {
  sha: string;
  bytes: number | null;
  encoding: string | null;
  updatedAt: string | null;
  location: string;
  objects?: number | null;
  setBytes?: number | null;
};


// The Level-1 builds land on merge; 30s keeps "watch it populate" honest
// without hammering the backend (which itself reloads every 10 min + on
// explicit refresh). Polling pauses while the tab is hidden.
const POLL_MS = 30 * 1000;

export const useKnowledgeGraph = (opts?: { pausePolling?: boolean }) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [data, setData] = useState<GlobalGraphResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [repos, setRepos] = useState<string[]>([]);
  // A broken store must never read as an empty store: /repos failures land
  // here and the panel renders them instead of "no graphs yet".
  const [listError, setListError] = useState<string | null>(null);
  // Store-side latest pointer per repo (from /repos polling): drives the
  // "newer build available" affordances and background cache refreshes.
  const [latestShas, setLatestShas] = useState<Record<string, string>>({});
  // Store-side publish time per repo (the latest pointer's own mtime —
  // the pointer moves last in a publish, so this IS when the graph landed).
  const [latestUpdated, setLatestUpdated] = useState<Record<string, string>>({});
  // Fleet-health status per repo (PLAT-1702), absent until a repo's first
  // post-producer build — consumers must render that as "no record".
  const [healthStatuses, setHealthStatuses] = useState<
    Record<string, RepoHealthStatus>
  >({});
  // name -> prefetch entry. Docs cache here for instant graph switching; the
  // stats surface in the graph-list panel as they land.
  const [entries, setEntries] = useState<Record<string, RepoGraphEntry>>({});
  const docCache = useRef(new Map<string, KgDoc>());
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  // While a Level-1 graph is on screen the global poll is pure console noise
  // (a 503 every 30s until Level-2 ships); pause it, resume on dismiss.
  const paused = useRef(false);
  paused.current = opts?.pausePolling ?? false;
  // The fleet build the data on screen came from — mirrors
  // data.summary.built_at_commit so cache invalidation on a build move can
  // happen outside the setData updater (updaters must stay pure).
  const globalBuild = useRef<string | null>(null);

  const load = useCallback(
    async (loadOpts?: { forceBackendRefresh?: boolean }) => {
      try {
        const base = await discoveryApi.getBaseUrl('knowledge-graph');
        if (loadOpts?.forceBackendRefresh) {
          // Throttled server-side (~30s); a 429 just means the scheduled
          // load is fresh enough, so fall through to the read either way.
          await fetchApi.fetch(`${base}/refresh`, { method: 'POST' }).catch(() => {});
        }
        const res = await fetchApi.fetch(`${base}/global`);
        if (res.status === 503) {
          const body = await res.json().catch(() => ({}));
          setError(
            body?.lastError
              ? `no graph loaded: ${body.lastError}`
              : 'no graph loaded yet (artifact store unconfigured or empty)',
          );
          // A restarted pod answers 503 until its first load lands — keep
          // whatever fleet graph is already on screen, null only from cold.
          setData(prev => prev ?? null);
          return;
        }
        if (!res.ok) throw new Error(`GET /global -> ${res.status}`);
        const body = (await res.json()) as GlobalGraphResponse;
        // The /global artifact routes serve whichever fleet build the
        // backend has loaded, so when built_at_commit moves the cached
        // dirtree describes a tree the shard routes no longer serve — drop
        // it so a long-lived tab never drills an old tree against new
        // shards.
        const nextBuild = body.summary?.built_at_commit ?? null;
        if (globalBuild.current && nextBuild && globalBuild.current !== nextBuild) {
          dirtreeCache.current.delete('global');
        }
        globalBuild.current = nextBuild;
        setData(body);
        setError(null);
      } catch (e) {
        // Keep the last rendered graph on transient failures; surface the error.
        setError(String((e as Error).message ?? e));
      } finally {
        setLoading(false);
      }
    },
    [discoveryApi, fetchApi],
  );

  useEffect(() => {
    load();
    timer.current = setInterval(() => {
      if (document.visibilityState === 'visible' && !paused.current) load();
    }, POLL_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [load]);

  // Repos with a Level-1 graph + their latest-pointer shas, polled every
  // 60s (backend caches the pointer sweep, so polling multiplies nothing).
  // Identity everywhere is the org/name slug — the store is org-scoped.
  // New builds land here first; consumers decide whether to swap in place.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const base = await discoveryApi.getBaseUrl('knowledge-graph');
        const res = await fetchApi.fetch(`${base}/repos`);
        if (cancelled) return;
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setListError(body?.error ?? `GET /repos -> ${res.status}`);
          return; // keep the last good list
        }
        const body = (await res.json()) as {
          repos?: {
            org: string;
            name: string;
            sha: string | null;
            updatedAt?: string | null;
            status?: RepoHealthStatus | null;
          }[];
          stale?: boolean;
        };
        if (!Array.isArray(body.repos)) {
          setListError('malformed /repos response');
          return;
        }
        setRepos(body.repos.map(r => `${r.org}/${r.name}`));
        const shas: Record<string, string> = {};
        const updated: Record<string, string> = {};
        const health: Record<string, RepoHealthStatus> = {};
        for (const r of body.repos) {
          if (r.sha) shas[`${r.org}/${r.name}`] = r.sha;
          if (r.updatedAt) updated[`${r.org}/${r.name}`] = r.updatedAt;
          if (r.status?.outcome) health[`${r.org}/${r.name}`] = r.status;
        }
        setLatestShas(shas);
        setLatestUpdated(updated);
        setHealthStatuses(health);
        // stale=true is the backend serving last-known-good over a store
        // failure — keep the list, but the outage must stay visible.
        setListError(
          body.stale ? 'graph store unreachable — showing last-known-good list' : null,
        );
      } catch (e) {
        // Keep the last good list; surface the failure so an outage can't
        // masquerade as "no graphs published yet".
        if (!cancelled) setListError(String((e as Error).message ?? e));
      }
    };
    poll();
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') poll();
    }, POLL_MS * 2);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [discoveryApi, fetchApi]);

  // Level-1 drill-down: fetch one repo's symbol graph (the backend resolves
  // the store's `latest` pointer), memoized in docCache so panel switches and
  // re-visits are instant. Until Level-2 lands, this is also how the page
  // shows a live graph when no global artifact exists yet.
  const statsOf = (doc: KgDoc) => ({
    symbols: doc.nodes.length,
    edges: (doc.links ?? doc.edges ?? []).length,
    files: new Set(doc.nodes.map(n => n.source_file).filter(Boolean)).size,
    sha: doc.built_at_commit,
  });

  // summary.json is tiny and answers "how big is this repo" before anyone
  // commits to the full doc; null is cached too (legacy repos without one).
  // Stored-object provenance (size/encoding/location/publish time), keyed by
  // slug@sha so a new publish refetches and a poll-only cycle stays free.
  const metaCache = useRef(new Map<string, KgRepoMeta | null>());
  const loadRepoMeta = useCallback(
    async (slug: string): Promise<KgRepoMeta | null> => {
      const key = `${slug}@${latestShas[slug] ?? ''}`;
      if (metaCache.current.has(key)) return metaCache.current.get(key) ?? null;
      try {
        const base = await discoveryApi.getBaseUrl('knowledge-graph');
        const res = await fetchApi.fetch(`${base}/repo/${slug}/meta`);
        if (res.ok) {
          const meta = (await res.json()) as KgRepoMeta;
          // Cache under the sha the response actually describes: the
          // backend's own 60s cache can serve the previous build right
          // after a publish, and pinning that under the new sha would show
          // wrong provenance for the rest of the session. A mismatched
          // response stays uncached and the next call retries.
          metaCache.current.set(`${slug}@${meta.sha}`, meta);
          return meta;
        }
        // 404/501 are definitive (nothing published / source can't stat);
        // 5xx is a store blip and must retry, so it is never cached.
        if (res.status === 404 || res.status === 501) {
          metaCache.current.set(key, null);
        }
        return null;
      } catch {
        // Provenance is garnish — a failed stat must never block the graph.
        return null;
      }
    },
    [discoveryApi, fetchApi, latestShas],
  );

  const summaryCache = useRef(new Map<string, KgSummary | null>());
  const loadSummary = useCallback(
    async (slug: string, opts2?: { force?: boolean }): Promise<KgSummary | null> => {
      if (!opts2?.force && summaryCache.current.has(slug)) {
        return summaryCache.current.get(slug)!;
      }
      const [org, name] = slug.split('/');
      if (!org || !name) return null;
      const base = await discoveryApi.getBaseUrl('knowledge-graph');
      const res = await fetchApi.fetch(
        `${base}/repo/${encodeURIComponent(org)}/${encodeURIComponent(name)}/artifact/summary`,
      );
      if (res.status === 404) {
        summaryCache.current.set(slug, null);
        return null;
      }
      if (!res.ok) throw new Error(`GET summary for ${slug} -> ${res.status}`);
      const sum = (await res.json()) as KgSummary;
      summaryCache.current.set(slug, sum);
      setEntries(prev => ({
        ...prev,
        [slug]: {
          ...prev[slug],
          summary: sum,
          stats: prev[slug]?.stats ?? {
            symbols: sum.nodes,
            files: sum.files,
            edges: sum.edges,
            sha: sum.built_at_commit,
          },
          // A giant is terminal here: the overview never needs the doc.
          status: sum.dirtree ? 'ready' : prev[slug]?.status ?? 'pending',
        },
      }));
      return sum;
    },
    [discoveryApi, fetchApi],
  );

  const dirtreeCache = useRef(new Map<string, KgDirtree>());
  const loadDirtree = useCallback(
    async (slug: string): Promise<KgDirtree> => {
      const cached = dirtreeCache.current.get(slug);
      if (cached) return cached;
      const base = await discoveryApi.getBaseUrl('knowledge-graph');
      // 'global' is the fleet-scale graph: same artifact contract, its own
      // routes (PLAT-1646).
      const [org, name] = slug.split('/');
      const url =
        slug === 'global'
          ? `${base}/global/artifact/dirtree`
          : `${base}/repo/${encodeURIComponent(org)}/${encodeURIComponent(name)}/artifact/dirtree`;
      const res = await fetchApi.fetch(url);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error ?? `GET dirtree for ${slug} -> ${res.status}`);
      }
      const tree = (await res.json()) as KgDirtree;
      dirtreeCache.current.set(slug, tree);
      return tree;
    },
    [discoveryApi, fetchApi],
  );

  const loadDirShard = useCallback(
    async (slug: string, key: string) => {
      const [org, name] = slug.split('/');
      const base = await discoveryApi.getBaseUrl('knowledge-graph');
      const url =
        slug === 'global'
          ? `${base}/global/dir/${encodeURIComponent(key)}`
          : `${base}/repo/${encodeURIComponent(org)}/${encodeURIComponent(name)}/dir/${encodeURIComponent(key)}`;
      const res = await fetchApi.fetch(url);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error ?? `GET dir shard ${key} -> ${res.status}`);
      }
      return (await res.json()) as import('./types').KgDoc;
    },
    [discoveryApi, fetchApi],
  );

  const loadRepoGraph = useCallback(
    async (slug: string, opts2?: { force?: boolean }): Promise<KgDoc> => {
      const cached = docCache.current.get(slug);
      if (cached && !opts2?.force) {
        // A failed background refresh may have left status:'error' while the
        // doc still serves fine from cache — repair the label on the way out.
        setEntries(prev =>
          prev[slug]?.status === 'ready'
            ? prev
            : { ...prev, [slug]: { status: 'ready', doc: cached, stats: statsOf(cached) } },
        );
        return cached;
      }
      const parts = slug.split('/');
      const [org, name] = parts;
      // Exactly two segments: a 3-segment deep link would otherwise fetch
      // /repo/<org>/<name> and label the graph with the garbage tail.
      if (parts.length !== 2 || !org || !name) {
        throw new Error(`not an org/name slug: "${slug}"`);
      }
      setEntries(prev => ({ ...prev, [slug]: { ...prev[slug], status: 'loading' } }));
      try {
        const base = await discoveryApi.getBaseUrl('knowledge-graph');
        const res = await fetchApi.fetch(
          `${base}/repo/${encodeURIComponent(org)}/${encodeURIComponent(name)}`,
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.error ?? `GET /repo/${slug} -> ${res.status}`);
        }
        const doc = (await res.json()) as KgDoc;
        docCache.current.set(slug, doc);
        setEntries(prev => ({
          ...prev,
          [slug]: { status: 'ready', doc, stats: statsOf(doc) },
        }));
        return doc;
      } catch (e) {
        const msg = String((e as Error).message ?? e);
        setEntries(prev => ({
          ...prev,
          [slug]: { ...prev[slug], status: 'error', error: msg },
        }));
        throw e;
      }
    },
    [discoveryApi, fetchApi],
  );

  // Prefetch every listed graph once the repo list lands (2-wide, on idle):
  // the JSONs are cheap relative to layout, and a warm docCache makes every
  // panel selection instant. Failures stay per-entry; selection retries.
  useEffect(() => {
    if (!repos.length) return undefined;
    let cancelled = false;
    setEntries(prev => {
      const next = { ...prev };
      for (const r of repos) if (!next[r]) next[r] = { status: 'pending' };
      return next;
    });
    const idle = (cb: () => void) =>
      'requestIdleCallback' in window
        ? (window as any).requestIdleCallback(cb, { timeout: 2000 })
        : setTimeout(cb, 250);
    idle(async () => {
      const queue = [...repos];
      const worker = async () => {
        while (queue.length && !cancelled) {
          const name = queue.shift()!;
          if (docCache.current.has(name)) continue;
          // Summary first: it is ~1 KB and marks giants, whose full doc must
          // never be prefetched (that is the 500-and-hang class of bug).
          // Fail CLOSED on a summary error: an unknown-size repo is treated
          // as possibly giant — skip the doc prefetch, selection retries.
          let sum: KgSummary | null;
          try {
            sum = await loadSummary(name);
          } catch {
            continue;
          }
          if (cancelled) break;
          if (sum?.dirtree) continue;
          await loadRepoGraph(name).catch(() => {});
        }
      };
      await Promise.all([worker(), worker()]);
    });
    return () => {
      cancelled = true;
    };
  }, [repos, loadRepoGraph, loadSummary]);

  // When the store's pointer moves for a repo we already cached, refresh it
  // in the background so the panel stats and the next open are current. The
  // on-screen graph is NOT swapped here — the page offers that explicitly.
  useEffect(() => {
    const staleDocs = Object.entries(latestShas).filter(([name, sha]) => {
      const doc = docCache.current.get(name);
      return doc?.built_at_commit && !shaMatches(doc.built_at_commit, sha);
    });
    const staleSummaries = Object.entries(latestShas).filter(([name, sha]) => {
      const sum = summaryCache.current.get(name);
      return sum?.built_at_commit && !shaMatches(sum.built_at_commit, sha);
    });
    if (!staleDocs.length && !staleSummaries.length) return;
    (async () => {
      // Summaries and dirtrees follow the pointer like docs do.
      const refreshed = new Set<string>();
      for (const [name] of staleSummaries) {
        dirtreeCache.current.delete(name);
        try {
          await loadSummary(name, { force: true });
          refreshed.add(name);
        } catch {
          /* retried on the next pointer poll */
        }
      }
      for (const [name] of staleDocs) {
        // A repo can cross the giant threshold on a new build: consult the
        // summary BEFORE force-refetching what might now be a huge doc.
        // (Already force-refreshed above -> the cache is current.)
        let sum: KgSummary | null;
        try {
          sum = await loadSummary(name, { force: !refreshed.has(name) });
        } catch {
          continue; // unknown size — keep last-known-good, never fetch blind
        }
        if (sum?.dirtree) {
          docCache.current.delete(name);
          continue;
        }
        await loadRepoGraph(name, { force: true }).catch(() => {});
      }
    })();
  }, [latestShas, loadRepoGraph, loadSummary]);

  return {
    data,
    error,
    loading,
    repos,
    listError,
    latestShas,
    latestUpdated,
    healthStatuses,
    entries,
    refresh: () => load({ forceBackendRefresh: true }),
    loadRepoMeta,
    loadRepoGraph,
    loadSummary,
    loadDirtree,
    loadDirShard,
  };
};

// Orgs come from the store itself now (repo/<org>/<name>/ layout) — the old
// catalog-annotation join is gone; groupReposByOrg keeps only a defensive
// "other" bucket for slugs missing an org segment.
